// Centralized guide configuration data for all guide modals

// Import SEO guide screenshots
import rowStep1 from '../../Assets/seoGuid/rowStep/step1.png';
import rowStep2 from '../../Assets/seoGuid/rowStep/step2.png';
import rowStep3 from '../../Assets/seoGuid/rowStep/step3.png';
import rowStep4 from '../../Assets/seoGuid/rowStep/step4.png';
import rowStep5 from '../../Assets/seoGuid/rowStep/step5.png';

import excelStep1 from '../../Assets/seoGuid/excelStep/excelstep1.png';
import excelStep2 from '../../Assets/seoGuid/excelStep/excelstep2.png';
import excelStep3 from '../../Assets/seoGuid/excelStep/excelstep3.png';
import excelStep4 from '../../Assets/seoGuid/excelStep/excelstep4.png';

// 1. SEO Metadata Guide
export const seoGuideTabs = [
  {
    id: 'row',
    label: '✍️ Row-by-Row Editing',
    title: 'Row-by-Row Inline Editing',
    subtitle: 'Learn how to search, modify, format, and save individual SEO metadata rows directly in the application grid.',
    steps: [
      {
        number: 1,
        title: 'Filter & Search Records',
        description: 'First, select the page Type, Language, and Country filters to load the metadata. To find a specific record, select a column in the "SEARCH IN" dropdown and enter your query in the search bar.',
        image: rowStep5
      },
      {
        number: 2,
        title: 'Click & Edit Inline',
        description: 'Simply click inside any editable cell (such as Meta Title, Alt Tag, etc.) to start typing. The cell content will update instantly. Modified cells are visually highlighted to indicate unsaved changes.',
        image: rowStep1
      },
      {
        number: 3,
        title: 'Dynamic Chip Suggestions',
        description: 'For fields marked with a ◈ badge, focusing on the cell opens a "Suggested Chips" menu. Click any chip option to automatically insert dynamic tags (like [product_name] or [price]) at your cursor.',
        image: rowStep2
      },
      {
        number: 4,
        title: 'Rich Text Editor Popup',
        description: 'Fields marked with a ✏ icon (e.g. Meta Description) can be edited in a rich formatting layout. Click the cell to open the WYSIWYG editor modal, format your content, and click "Apply".',
        image: rowStep3
      },
      {
        number: 5,
        title: 'Save Changes & Manage Status',
        description: 'Unsaved rows are highlighted in yellow. Save your edits by clicking the "Save" button on the right of the row, or bulk-save everything via the "Save All" button. You can also toggle the status (🟢/⚪) or delete entries.',
        image: rowStep4
      }
    ]
  },
  {
    id: 'excel',
    label: '📊 Excel Bulk Import / Export',
    title: 'Excel Bulk Operations',
    subtitle: 'Follow this walkthrough to download, edit offline, and upload bulk Excel spreadsheets to manage metadata at scale.',
    steps: [
      {
        number: 1,
        title: 'Export Filtered Template',
        description: 'First, select your Page Type, Language, and Country filters. Click the green "Export Filtered" button in the grid toolbar to download a pre-formatted Excel template populated with current records.',
        image: excelStep1
      },
      {
        number: 2,
        title: 'Update Content in Excel',
        description: 'Open the spreadsheet in Microsoft Excel and update your values. Do not change the headers or modify structural columns like SEO ID or Ref ID. Ensure the sheet names remain unchanged.',
        image: excelStep2
      },
      {
        number: 3,
        title: 'Upload Spreadsheet',
        description: 'Click the "Import" button next to the filters at the top of the page. In the dialog, select your edited Excel file and click the blue "Upload" button to submit your changes.',
        image: excelStep3
      },
      {
        number: 4,
        title: 'Success Verification',
        description: 'The backend will validate the document structure and apply changes. On success, a notification will appear, and the main grid will refresh automatically to show the new imported values.',
        image: excelStep4
      }
    ]
  }
];

// 2. Homepage Content Section Guide
export const homepageGuideTabs = [
  {
    id: 'duplicate',
    label: '👯 Duplicating Records',
    title: 'Record Duplication Guide',
    subtitle: 'Follow these steps to duplicate records and structures across different sections.',
    steps: [
      {
        number: 1,
        title: 'Open Duplication Dialog',
        description: 'Click the Settings Cog icon in the Actions column of the row you wish to duplicate, then select the "Duplicate" option from the dropdown menu.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Select Destination Page',
        description: 'In the modal popup that appears, select the target Page Name from the dropdown options to specify where the duplicate records should be copied.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Duplication Level Options (Toggles)',
        description: 'Choose your duplication method using the toggles:\n\n• Duplicate this particular one record: Copies only the chosen individual record.\n• Duplicate this hierarchy records: Copies the record along with its entire children/parent tree hierarchy.',
        isPlaceholder: true
      }
    ]
  },
  {
    id: 'create',
    label: '➕ Creating New Content',
    title: 'Creating and Mapping Content',
    subtitle: 'Learn how to create a new content section and link it to your page structures.',
    steps: [
      {
        number: 1,
        title: 'Create Content Record',
        description: 'First, click the blue "+" button in the table header to open the Content Creation modal. Fill in the required content details (Description, Image, Date Range, etc.) and save it.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Navigate to Structure Page',
        description: 'Go to the "/Structure" page (Site Structure / Page Info) using the sidebar navigation menu.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Select Target Page & Click Create',
        description: 'Choose the page where you want to embed your new section, then click the "Create" button to open the structure attachment modal.',
        isPlaceholder: true
      },
      {
        number: 4,
        title: 'Select Section from Dropdown',
        description: 'In the structure modal, look for the "Section" dropdown option. Select your newly created content record from the list to bind it to the page layout.',
        isPlaceholder: true
      }
    ]
  }
];

// 3. Category FAQ Guide
export const categoryGuideTabs = [
  {
    id: 'faqs',
    label: '❓ Managing FAQs & Remarks',
    title: 'Category FAQs & Localization Walkthrough',
    subtitle: 'Learn how to add localized FAQs, specify country active states, and write footer remarks for your categories.',
    steps: [
      {
        number: 1,
        title: 'Select Category to Edit',
        description: 'First, locate the Category you want to update inside the Category Listing grid.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Open Configuration Modal',
        description: 'Click on the Actions Settings Cog dropdown for that Category row and select the "Edit" option to open the management modal window.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Navigate to Faqs Tab',
        description: 'Inside the Category modal, click the "Faqs" tab. Here you will see sub-tabs representing each country flags (e.g. AE, SA, QA) to localized FAQ contents.',
        isPlaceholder: true
      },
      {
        number: 4,
        title: 'Add & Save FAQs',
        description: 'Click the "Add More FAQ" button. Input the Question (Section Name), specify Ordering, set Active Status, check Applicable Country flags, write the Answer in the rich text editor, and click the "Save All FAQs" button.',
        isPlaceholder: true
      },
      {
        number: 5,
        title: 'Add Country Wise Remarks',
        description: 'Click the "Remark" button inside the country tab. In the popup editor modal, type the disclaimer or notes that you want to display in the FAQs page footer, and click "Save Remark".',
        isPlaceholder: true
      }
    ]
  },
  {
    id: 'translation',
    label: '🌐 Category Translation',
    title: 'Translating Category Content',
    subtitle: 'Follow these steps to translate category descriptions, titles, and localized details into other languages.',
    steps: [
      {
        number: 1,
        title: 'Go to Translation Page',
        description: 'Navigate to the "/Translation" management page from the sidebar menu.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Select Category Module',
        description: 'From the module select dropdown at the top of the Translation page, select "Category".',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Select Language & Country',
        description: 'Select the target Language and Country that you wish to translate category content for.',
        isPlaceholder: true
      },
      {
        number: 4,
        title: 'Translate & Save Content',
        description: 'The grid will render category records. Enter the translated texts in the corresponding translation boxes, and click Save.',
        isPlaceholder: true
      }
    ]
  }
];

// 4. Bulk Upload Guide
export const bulkGuideTabs = [
  {
    id: 'bulk_upload',
    label: '📦 Bulk Price Upload',
    title: 'Bulk Pricing Update Guide',
    subtitle: '💡 With this tool, you can upload bulk Excel sheets to quickly update the price of items on your ecommerce website.',
    steps: [
      {
        number: 1,
        title: 'Export Data according to Language',
        description: 'Click the "Bulk Export" button in the table header. Select the target Language, Country, and Product categories, then download the generated template file.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Filter and Edit the Data',
        description: 'Open the downloaded Excel sheet. Filter out the specific rows/items you want to update. Modify the price values as needed.\n\n⚠️ IMPORTANT: Do NOT change the sequence of columns or delete any columns, otherwise the upload verification will fail.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Save and Upload Excel',
        description: 'After completing data updates, save your Excel sheet. Click the "Add Bulk Upload" (+) button, enter your execution date/remarks, attach your saved Excel file, and click save to apply the new prices to the ecommerce site.',
        isPlaceholder: true
      }
    ]
  }
];

// 5. Header Management Guide
export const headerGuideTabs = [
  {
    id: 'header_mgmt',
    label: '📁 Header Navigation',
    title: 'Header Navigation Bar Management',
    subtitle: 'Learn how to manage parent headers, subheaders, active dates, and country flag alignments.',
    steps: [
      {
        number: 1,
        title: 'Add New Header Option',
        description: 'Click the blue "+" button in the header toolbar to open the Add Header Modal. Fill in the description name, set order weight, from/upto active dates, and click Save.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Localized Country Selection',
        description: 'Under the country flag cards, toggle (activate/deactivate) specific country ISO codes (like AE, SA, QA) to determine which regional front-ends display this header.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Manage Nesting & Parent Child',
        description: 'If creating a sub-menu, uncheck "Parent ?" and select the appropriate Parent Category from the dropdown options list.',
        isPlaceholder: true
      },
      {
        number: 4,
        title: 'Translate Menu Descriptions',
        description: 'To translate the header item into another language (e.g. Arabic), click the settings cog of the header row, select "Edit Language", write the translation, and click Save.',
        isPlaceholder: true
      }
    ]
  }
];

// 6. Footer Management Guide
export const footerGuideTabs = [
  {
    id: 'footer_mgmt',
    label: '📂 Footer Columns',
    title: 'Footer Links & Columns Management',
    subtitle: 'Step-by-step tutorial on defining footer groups, active links, ordering ranks, and country visibilities.',
    steps: [
      {
        number: 1,
        title: 'Create Footer Link or Parent Category',
        description: 'Click the blue "+" button in the listing header. Enter your footer link title, link URL path (if direct), and set active dates. Save to register the item.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Configure Footers Country Visibility',
        description: 'Click the country flags listed in the Details tab to toggle active visibilities for each country sub-site.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Translate Footer Content',
        description: 'Select the "Edit Language" option from the row Actions cog menu. Select your language (e.g. Arabic), insert the translated footer title/text, and click Save.',
        isPlaceholder: true
      }
    ]
  }
];

// 7. Menu Management Guide
export const menuGuideTabs = [
  {
    id: 'menu_mgmt',
    label: '☰ Sidebar Menus',
    title: 'Sidebar Navigation Drawer Management',
    subtitle: 'Manage permission routes, display titles, nested submenus, and language translations.',
    steps: [
      {
        number: 1,
        title: 'Add New Menu Item',
        description: 'Click the blue "+" button. Fill in the Menu Description (display title), definition route (e.g. category/faq), choose menu status, and click Save.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Manage Sub-menus & Parent Nesting',
        description: 'Select parent menu from the dropdown if you want to nest this entry under an existing navigation drawer dropdown category.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Edit Language Translations',
        description: 'Choose the "Edit" or language option inside the Actions cog dropdown, select the target language code, write the translated description, and submit.',
        isPlaceholder: true
      }
    ]
  }
];

// 8. Page Info Guide
export const pageInfoGuideTabs = [
  {
    id: 'page_info',
    label: '📄 Page Config',
    title: 'Site Page & Template Info Config',
    subtitle: 'Learn how to register dynamic subpages, custom template styles, and define localized detail content.',
    steps: [
      {
        number: 1,
        title: 'Add New Page Info Record',
        description: 'Click the blue "+" button in the table header to open the Add Page Modal. Enter the Page Description, unique route Slug, active status, ordering, and date range.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Assign Layout Template',
        description: 'Choose the appropriate Page Type or UI template code block that defines how the frontend app renders the content components.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Manage Translations',
        description: 'Click the Actions Cog dropdown on a page row and select "Edit Language" (e.g. for Arabic). Write translations and click Save.',
        isPlaceholder: true
      },
      {
        number: 4,
        title: 'Define Country Detail HTML',
        description: 'Under the country flag cards, add localized layout details (like custom HTML text or sliders) for each target regional country site.',
        isPlaceholder: true
      }
    ]
  }
];

// 9. Product Info Guide
export const productInfoGuideTabs = [
  {
    id: 'product_info',
    label: '🛍️ Products & Specs',
    title: 'Product Master Listing & Pricing Configurations',
    subtitle: 'Learn how to manage ecommerce product uploads, price matrixes, filter specs, galleries, and customization steps.',
    steps: [
      {
        number: 1,
        title: 'Upload New Product Info',
        description: 'Click the blue "+" button in the actions header to open the Product Modal. Enter the product title, order rank, active dates, select its primary Category dropdown, and click Save.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Configure Pricing Matrix',
        description: 'Click the Actions Cog dropdown on the product row and select "Price". Set base prices, currency values, dynamic calculation rates, and active country availability.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Upload Media Gallery & Steps',
        description: 'Inside the Actions Cog dropdown:\n\n• Select "Gallery" to upload high-res product slider images.\n• Select "Steps" to set up step-by-step customization options (e.g. fabric choice, measurement guides).',
        isPlaceholder: true
      },
      {
        number: 4,
        title: 'SEO & Translations',
        description: 'Use the "SEO" item to configure metadata tags, and the "Edit Language" item to write localized product descriptions.',
        isPlaceholder: true
      }
    ]
  }
];

// 10. Item Info Guide
export const itemInfoGuideTabs = [
  {
    id: 'item_info',
    label: '🏷️ Pricing Items',
    title: 'Showroom & Product Pricing Items Config',
    subtitle: 'Learn how to manage inventory items, item codes, brand associations, localized translations, and stock logs.',
    steps: [
      {
        number: 1,
        title: 'Create Pricing Item Record',
        description: 'Click the blue "+" button in the table header to open the Item Modal. Enter the item code, description, weight specifications, ordering sequence, and save it.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Map Family & Category',
        description: 'Select the primary Item Family and product Category dropdowns to bind this pricing item to the correct search filters.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Configure Gallery & Stock Logs',
        description: 'Click the row Actions Cog dropdown:\n\n• Select "Gallery" to upload high-res item images.\n• Select "Check Item Stock" to review real-time showroom levels in AE, SA, and QA.',
        isPlaceholder: true
      },
      {
        number: 4,
        title: 'Manage Translations & SEO',
        description: 'Use the "Edit Language" action option to translate item descriptions (like Arabic translations) and configure "SEO" tags.',
        isPlaceholder: true
      }
    ]
  }
];

// 11. Filter Type Guide
export const filterTypeGuideTabs = [
  {
    id: 'filter_type',
    label: '🎛️ Filter Categories',
    title: 'Sidebar Filter Categories Configurations',
    subtitle: 'Learn how to register search filters (e.g. Color, Material, Brand) and map localized title values.',
    steps: [
      {
        number: 1,
        title: 'Add Filter Category Group',
        description: 'Click the blue "+" button in the header toolbar. Enter the filter name, specify its type, and set the sequence rank weight.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Set Unique Filter Code',
        description: 'Assign a unique Filter Code key. This code maps the backend database attributes to frontend sidebar toggles.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Edit Language Translations',
        description: 'Click the row Actions Cog dropdown, choose "Edit Language", select language code (e.g. Arabic), write the translated filter title, and click Save.',
        isPlaceholder: true
      }
    ]
  }
];

// 12. Family Info Guide
export const familyInfoGuideTabs = [
  {
    id: 'family_info',
    label: '👪 Family Grouping',
    title: 'Product Family Grouping & Management',
    subtitle: 'Learn how to define parent families, map collections/brands, set bulk uploads, and configure pricing rules.',
    steps: [
      {
        number: 1,
        title: 'Create Family Record',
        description: 'Click the blue "+" button in the table header to open the Add Family Modal. Fill out unique family code, description, collection type, brand name, sequence order, and click Save.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Configure Family Pricing Rules',
        description: 'Click the row Actions Cog dropdown and select "Price". Set up base dimensions, dynamic pricing rates, and applicable country currency overrides.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Bulk Pricing Operations',
        description: 'Select the "Bulk Upload" button next to the filters toolbar if you want to import or export family-wide templates.',
        isPlaceholder: true
      },
      {
        number: 4,
        title: 'Set Regional Country Access',
        description: 'Select country flag checkboxes inside the creation form to toggle display active status on AE, SA, or QA sites.',
        isPlaceholder: true
      }
    ]
  }
];

// 13. Delivery Zone Guide
export const deliveryZoneGuideTabs = [
  {
    id: 'delivery_zone',
    label: '🚚 Delivery Fees',
    title: 'Regional Delivery & Installation Zones Config',
    subtitle: 'Learn how to manage delivery fees, installation zones, base cart limits, and active date constraints.',
    steps: [
      {
        number: 1,
        title: 'Add New Delivery Zone',
        description: 'Click the blue "+" button in the header toolbar to open the Add Zone Modal. Enter the zone description, set cart value boundaries (from/upto limits), input delivery and installation fees, and set active dates.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Define Pricing Thresholds',
        description: 'Set rules such as: charging a flat delivery fee for lower order cart values, or setting fees to 0 if the cart exceeds a specified threshold to enable free delivery.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Save and Verify Active State',
        description: 'Click Save, then verify that the Active state is enabled so that checkout operations can apply the calculation dynamically.',
        isPlaceholder: true
      }
    ]
  }
];

// 14. Delivery Zone City Guide
export const deliveryZoneCityGuideTabs = [
  {
    id: 'delivery_zone_city',
    label: '🗺️ City & Area Maps',
    title: 'City, Neighborhood, and Delivery Zone Mappings',
    subtitle: 'Walkthrough on mapping countries, provinces, cities, and specific neighborhood areas to delivery zones.',
    steps: [
      {
        number: 1,
        title: 'Open Add/Edit City Modal',
        description: 'Click the blue "+" button in the listing header toolbar to open the mapping modal.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Map Neighborhood to Delivery Zone',
        description: 'Select the target Country ISO, type or select State/Province, select City description, enter neighborhood Area, and choose the correct Delivery Zone fee structure to bind to this location.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Toggle Active Statuses',
        description: 'Toggle the City Active or Area Active switches to control courier delivery permissions for individual neighborhoods.',
        isPlaceholder: true
      }
    ]
  }
];

// 15. City List Guide
export const cityGuideTabs = [
  {
    id: 'city_list',
    label: '🏙️ Cities Config',
    title: 'City Master Configuration & Mapping',
    subtitle: 'Learn how to register new cities under parent states, select main-city indicators, configure delivery zones, and edit translations.',
    steps: [
      {
        number: 1,
        title: 'Add New City Record',
        description: 'Click the blue "+" button. Choose the Country ISO, select State/Province from the dropdown, type the City description, choose if it is a Main City, map the Delivery Zone, and click Save.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Edit Language Translation',
        description: 'Click the row Actions Cog dropdown and select "Edit Language". Choose target language code (e.g. Arabic), enter the localized city name, and click Save.',
        isPlaceholder: true
      }
    ]
  }
];

// 16. Area List Guide
export const areaGuideTabs = [
  {
    id: 'area_list',
    label: '🏘️ Areas Config',
    title: 'Neighborhood & Sub-district Areas Configuration',
    subtitle: 'Learn how to register neighborhood areas, map them to parent states/cities, and configure language translations.',
    steps: [
      {
        number: 1,
        title: 'Add New Area Neighborhood',
        description: 'Click the blue "+" button in the listing header. Select Country ISO, State/Province, parent City description, type the neighborhood Area name, and click Save.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Translate Area Title',
        description: 'Click the row Actions Cog dropdown and select "Edit Language". Select the target language, enter the translated area description, and click Save.',
        isPlaceholder: true
      }
    ]
  }
];

// 17. Showroom Guide
export const showroomGuideTabs = [
  {
    id: 'showroom_list',
    label: '🏬 Showrooms Config',
    title: 'Showroom Location & Contact Configuration',
    subtitle: 'Learn how to manage physical showroom coordinates, store maps, opening hours, and contact details.',
    steps: [
      {
        number: 1,
        title: 'Create Showroom Record',
        description: 'Click the blue "+" button in the table header. Enter address title, choose target Country ISO and City location, add phone numbers, physical address text, and toggle active status.',
        isPlaceholder: true
      },
      {
        number: 2,
        title: 'Configure Coordinates & Timings',
        description: 'Specify showroom coordinates (latitude & longitude) for Google Maps, type showroom opening/closing hours, set sorting order sequence rank, and click Save.',
        isPlaceholder: true
      },
      {
        number: 3,
        title: 'Translate Store Details',
        description: 'Click the row Actions Cog dropdown and select "Edit Language" to write the showroom address and details in alternative languages.',
        isPlaceholder: true
      }
    ]
  }
];
